package dev.lingvo.site.components.billing

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.BillingInterval
import dev.lingvo.shared.BillingOverviewDto
import dev.lingvo.shared.PlanStatus
import dev.lingvo.site.components.statusBadgeVariant
import dev.lingvo.site.components.statusWords
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSectionTitle
import dev.lingvo.site.ds.LStatRow
import dev.lingvo.site.ds.LStatRowItem
import dev.lingvo.site.ds.Radius
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.formatCount
import dev.lingvo.site.ds.tabularNumbers
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/** Loaded billing facts only: grants remain separate and overuse never changes the reported total. */
@Composable
fun BillingPlanOverview(data: BillingOverviewDto, creditCaption: String) {
    Div(
        Modifier.fillMaxWidth().styleModifier {
            property("display", "grid")
            property("grid-template-columns", "repeat(auto-fit, minmax(min(100%, 320px), 1fr))")
            property("gap", Space.LG.toString())
        }.toAttrs(),
    ) {
        LCard(Modifier.fillMaxWidth()) {
            Column(Modifier.fillMaxWidth().gap(Space.LG)) {
                LMuted(t("billing.billing_plan_overview.current_plan", "Current plan"))
                Row(Modifier.gap(Space.SM).flexWrap(FlexWrap.Wrap), verticalAlignment = Alignment.CenterVertically) {
                    LSectionTitle(data.plan.displayName, Modifier.fontSize(TypeScale.HeadingSmall))
                    LBadge(data.status.statusWords(), variant = data.status.statusBadgeVariant())
                }
                // Both sides of the seat count. The interesting fact is whether they agree — and a
                // customer should hear about a mismatch from us, not from an invoice.
                LMuted(
                    buildString {
                        append(
                            tPlural(
                                "app.settings.billing.count.owner_admin_seat",
                                data.seats.held,
                                "You have {count} owner and admin seat.",
                                "You have {count} owner and admin seats.",
                            ),
                        )
                        // The separator moves into each message. A leading ". " appended by the
                        // caller is punctuation a translator cannot see, cannot move, and cannot
                        // drop for a language that does not join clauses that way.
                        append(' ')
                        when {
                            data.seats.billed == null ->
                                append(t("billing.billing_plan_overview.no_subscription_yet", "No subscription yet."))
                            data.seats.syncing ->
                                append(t("billing.billing_plan_overview.updating_the_billed_count", "Updating the billed count…"))
                            data.seats.matches -> append(
                                tf(
                                    "app.settings.billing.paddle_is_billing_count",
                                    "Paddle is billing {count}.",
                                    "count" to data.seats.billed,
                                ),
                            )
                            else -> append(
                                tf(
                                    "app.settings.billing.paddle_is_billing_count_correcting",
                                    "Paddle is billing {count} — we're correcting this.",
                                    "count" to data.seats.billed,
                                ),
                            )
                        }
                    },
                )

                LMuted(
                    if (data.interval == BillingInterval.MONTH) {
                        tf(
                            "app.settings.billing.value.monthly_list_price",
                            "{price} per owner or admin each month — list price, before tax and currency conversion.",
                            "price" to data.listPricePerSeat.display,
                        )
                    } else {
                        tf(
                            "app.settings.billing.value.annual_list_price",
                            "{price} per owner or admin each year — list price, before tax and currency conversion.",
                            "price" to data.listPricePerSeat.display,
                        )
                    },
                )
                LMuted(
                    data.renewsAt?.let { date ->
                        if (data.status == PlanStatus.CANCELING) {
                            tf("app.settings.billing.value.ends_on", "Plan ends {date}.", "date" to date.take(10))
                        } else {
                            tf("app.settings.billing.value.renews_on", "Next renewal: {date}.", "date" to date.take(10))
                        }
                    } ?: if (data.hasSubscription) {
                        t("billing.billing_plan_overview.a_renewal_date_is_not_available", "A renewal date is not available.")
                    } else {
                        t("billing.billing_plan_overview.no_card_no_expiry", "No card, no expiry.")
                    },
                )
                LMuted(t("billing.billing_plan_overview.translators_and_viewers_are_free_additional", "Translators and viewers are free. Additional owners and admins are paid seats on Studio and Team."))
            }
        }
        LCard(Modifier.fillMaxWidth()) {
            Column(Modifier.fillMaxWidth().gap(Space.LG)) {
                LSectionTitle(t("billing.billing_plan_overview.this_months_credits", "This month's credits"), Modifier.fontSize(TypeScale.HeadingSmall))
                Row(Modifier.gap(Space.SM).flexWrap(FlexWrap.Wrap), verticalAlignment = Alignment.CenterVertically) {
                    Span(Modifier.fontSize(TypeScale.Heading).fontWeight(600).tabularNumbers().toAttrs()) {
                        Text(formatCount(data.creditsRemaining))
                    }
                    LMuted(t("billing.billing_plan_overview.remaining", "remaining"))
                }
                CreditUsageGraphic(data)
                LStatRow(
                    rows = listOf(
                        LStatRowItem(t("billing.billing_plan_overview.included", "Included"), formatCount(data.creditsIncluded)),
                        LStatRowItem(t("billing.billing_plan_overview.granted", "Granted"), formatCount(data.creditsGranted)),
                        LStatRowItem(t("billing.billing_plan_overview.used", "Used"), formatCount(data.creditsUsed)),
                        LStatRowItem(t("billing.billing_plan_overview.remaining_2", "Remaining"), formatCount(data.creditsRemaining), emphasis = true),
                    ),
                    caption = creditCaption,
                )
                if (data.creditsRemaining == 0) {
                    LMuted(t("billing.billing_plan_overview.your_shared_key_allowance_is_used", "Your shared-key allowance is used up. Use your own configured provider key, wait for the reset or compare plans."))
                    LMuted(t("billing.billing_plan_overview.manual_translation_import_export_review_and", "Manual translation, import, export, review and QA still work."))
                }
                LMuted(t("billing.billing_plan_overview.included_and_granted_credits_reset_on", "Included and granted credits reset on the 1st of each month (UTC). No automatic overage charges."))
            }
        }
    }
}

@Composable
private fun CreditUsageGraphic(data: BillingOverviewDto) {
    val palette = ColorMode.current.toSitePalette()
    val allowance = (data.creditsIncluded + data.creditsGranted).coerceAtLeast(1)
    val paintedUsed = data.creditsUsed.coerceIn(0, allowance)
    Row(Modifier.fillMaxWidth().gap(Space.SM).flexWrap(FlexWrap.Wrap)) {
        LMuted(t("billing.billing_plan_overview.credits_used", "Credits used"))
        Span(Modifier.tabularNumbers().toAttrs()) {
            Text(
                tf(
                    "app.settings.billing.value.used_rail_total",
                    "{used} of {allowance}",
                    "used" to formatCount(data.creditsUsed),
                    "allowance" to formatCount(data.creditsIncluded + data.creditsGranted),
                ),
            )
        }
    }
    // This drawing is static and decorative; the adjacent real table preserves every exact value.
    Div(
        Modifier.fillMaxWidth().height(10.px).borderRadius(Radius.Pill)
            .backgroundColor(palette.border).attr("aria-hidden", "true").toAttrs(),
    ) {
        Div(
            Modifier.height(10.px).borderRadius(Radius.Pill)
                .width((paintedUsed * 100.0 / allowance).percent)
                .backgroundColor(palette.accent).toAttrs(),
        )
    }
}
